/**
 * The receipt's three-stage life: **panel → toast → tab** (`components.md`
 * §2.6).
 *
 * ```
 *  T+73          T+74                      T+81
 *   │              │                         │
 *   receipt in     animates down into        toast dismissed;
 *   the secondary  a 6s toast                the Settled tab is
 *   surface, ~1s                             the durable record
 * ```
 *
 * §4.2 of `docs/10` escalated a contradiction here — `docs/03` §6.2 put the
 * receipt's end at `T+81` while §2.6 opened by refusing an 8-second linger — and
 * §F24 dissolved it rather than settling it: the two texts were arguing about
 * the *ticket slot*, and the receipt no longer touches it. `T+81` is simply
 * where its on-screen life ends, and this component is the ~8 seconds in
 * between.
 *
 * **A user holding both BTC and ETH at the same anchor gets two receipts,
 * stacked, ordered by absolute net PnL, never merged into a summary.** Each
 * market resolved on its own settlement window, and a combined figure would
 * describe a market that does not exist.
 *
 * ## Why the age comes off `settled_at_ms`
 *
 * Because it is on the payload, and because the alternative — stamping arrival
 * in this tab — is state that has to be written from an effect and read during
 * render, which is a receipt that appears one clock tick late on exactly the
 * render it arrived. The venue's own timestamp needs neither: `nowMs` is the
 * corrected server clock (`Date.now()` is banned outside `lib/clock/`,
 * invariant 7), so the two are in the same frame of reference and the window is
 * "settled less than a second and a bit ago" rather than "seen recently".
 *
 * It also bounds the reconnect case for free. `settlements` is at-least-once and
 * a resubscribe may legitimately replay a snapshot of the session's settlements;
 * a tab that stamped arrival would fire a burst of toasts for markets that
 * resolved ten minutes ago. `TOAST_MAX_AGE_MS` is where that stops.
 *
 * ## Why it subscribes rather than being handed rows
 *
 * Invariant 2. `settlements` is quiet — one frame per market the account held —
 * but a subscription hoisted into `<TradeRoute>` re-renders the chart and the
 * ladder every time one lands, and the whole point of the split is that nothing
 * re-renders for a channel it does not draw.
 */

import { useEffect, useRef } from 'react'
import { useSettlements } from '@/app/use-runtime'
import { useServerNow } from '@/app/use-server-now'
import { pushToast } from '@/stores/ui'
import { cx } from '@/lib/cx'
import { Glyph } from '@/components/primitives'
import { formatSignedUsdc } from '@/lib/format'
import { SettlementReceipt, absNetPnlMicro, type Settlement } from './SettlementReceipt'

/** How long the receipt holds the secondary surface before it becomes a toast.
 *  §2.6: "~1s", and then it is out of the way of the market being traded. */
export const RECEIPT_SURFACE_MS = 1_200
/** §2.6's 6-second auto-dismiss, which is one second longer than every other
 *  toast in the product — see `Toast.dismissMs`. */
export const RECEIPT_TOAST_MS = 6_000
/**
 * How old a settlement may be and still raise a toast.
 *
 * `settlements` is at-least-once, so a reconnect can redeliver — and a real
 * server may answer a resubscribe with a snapshot of the session's settlements,
 * which without this would be a queue of toasts for markets that resolved ten
 * minutes ago. Generous enough to cover the whole resolution window plus a
 * reconnect, short enough that a replay is silent.
 */
export const TOAST_MAX_AGE_MS = 30_000

export interface SettlementReceiptStackProps {
  /**
   * Render the receipt in the surface for its ~1s, or only raise the toast.
   *
   * Off in the phone's one-line resolution bar, where a full receipt would
   * jump the book down for a second every minute. The toast is raised either
   * way — it comes from the effect below, not from what renders.
   */
  showReceipt?: boolean
  /**
   * A market whose settlement raises no toast, because something on screen is
   * already showing it — `<LastMarketCard>`, from 1120px up, names the market
   * it carries. A settlement of any other market (the other asset, or this one
   * arriving after the card has moved on) still toasts.
   */
  quietMarketId?: string | null
  className?: string
}

export function SettlementReceiptStack({
  showReceipt = true,
  quietMarketId = null,
  className,
}: SettlementReceiptStackProps) {
  const { rows: settlements, version } = useSettlements<Settlement>()
  const nowMs = useServerNow()

  /**
   * Settlements this tab has already toasted.
   *
   * A ref because nothing renders from it: the toast store is the external
   * system this effect synchronises *to*, and pushing twice for one settlement
   * would be the visible bug. Never state — a `setState` here would be a
   * cascading render for a value no element reads.
   */
  const toasted = useRef(new Set<string>())

  useEffect(() => {
    for (const settlement of settlements) {
      const id = idOf(settlement)
      if (id === null || toasted.current.has(id)) continue
      toasted.current.add(id)
      // Bounded: the log holds 500 and this follows it rather than growing for
      // the life of the tab.
      if (toasted.current.size > 1_000) toasted.current.clear()
      if (ageOf(settlement, nowMs) > TOAST_MAX_AGE_MS) continue
      if (quietMarketId !== null && settlement.market_id === quietMarketId) continue
      // Once, on first sight, and it outlives this component's own render of the
      // receipt — which is the point: the surface belongs to the next market's
      // resolution about forty seconds later, and the toast does not.
      pushToast(toastFor(settlement))
    }
    // **`version`, not `settlements`.** `LogStore.append` unshifts into the
    // array in place, so the rows' identity never changes and an effect keyed on
    // them runs once, on mount, against an empty list — which is a receipt toast
    // that never fires (§F25k). `nowMs` is read but not depended on: this effect
    // is about *arrival*, and re-running it four times a second would push no
    // toast and re-walk the list 240 times a minute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version])

  const fresh = settlements.filter((settlement) => ageOf(settlement, nowMs) < RECEIPT_SURFACE_MS)
    // Biggest move first (§2.6). `bigint`, because ordering money by a float is
    // how two receipts a cent apart swap places between renders.
    .sort((a, b) => (absNetPnlMicro(b) > absNetPnlMicro(a) ? 1 : absNetPnlMicro(b) < absNetPnlMicro(a) ? -1 : 0))

  if (!showReceipt || fresh.length === 0) return null
  return (
    <div className={cx('flex flex-col gap-px', className)}>
      {fresh.map((settlement) => (
        <SettlementReceipt key={idOf(settlement) ?? ''} settlement={settlement} />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------

/**
 * The toast, carrying the same numbers.
 *
 * `▲`/`▼` go through `<Glyph>` and never into the string — they exist only in
 * the mono face and only because a 2.6 KB patch file puts them there (§F17),
 * which is why `Toast.title` is a `ReactNode`.
 */
export function toastFor(settlement: Settlement): Parameters<typeof pushToast>[0] {
  const outcome = typeof settlement.outcome === 'string' ? settlement.outcome : 'UP'
  const void_ = outcome === 'VOID'
  const won = settlement.won === true
  const netPnl = typeof settlement.net_pnl === 'string' ? settlement.net_pnl : '0.000000'
  const marketId = typeof settlement.market_id === 'string' ? settlement.market_id : ''

  return {
    tone: void_ ? 'info' : won ? 'success' : 'warning',
    dismissMs: RECEIPT_TOAST_MS,
    title: void_ ? (
      'Market voided'
    ) : (
      <span className="flex items-center gap-1_5">
        <Glyph name={outcome === 'DOWN' ? 'down' : 'up'} label={null} />
        {outcome} · RESOLVED
      </span>
    ),
    detail: void_ ? 'refunded at entry cost' : formatSignedUsdc(netPnl),
    body: marketId,
  }
}

function idOf(settlement: Settlement): string | null {
  const id = settlement.settlement_id
  return typeof id === 'string' ? id : null
}

/** How long ago the venue settled this, on the corrected clock. `Infinity` when
 *  the payload carries no timestamp, which keeps an unstamped settlement out of
 *  the surface and out of the toast rather than pinning it there. */
function ageOf(settlement: Settlement, nowMs: number): number {
  const at = settlement.settled_at_ms
  return typeof at === 'number' ? nowMs - at : Infinity
}
